import React, {
  useCallback, useContext, useEffect, useRef, useState,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import {
  Box,
  Stack,
  Card,
  Typography,
  Button,
  TextField,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Divider,
  Skeleton,
} from '@mui/material';
import TvRoundedIcon from '@mui/icons-material/TvRounded';
import SmartphoneRoundedIcon from '@mui/icons-material/SmartphoneRounded';
import ComputerRoundedIcon from '@mui/icons-material/ComputerRounded';
import DevicesOtherRoundedIcon from '@mui/icons-material/DevicesOtherRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import AddToQueueRoundedIcon from '@mui/icons-material/AddToQueueRounded';
import { AppContext } from 'Context';
import { dataFetcher, endpoints } from 'Api';
import { sendHeartbeat } from 'Utils/deviceSession';
import { Pathname } from 'Routes';
import { V2SettingsShell } from '../../_shared/V2SettingsShell';

// Server timestamps are GMT+00. MySQL-style strings ("YYYY-MM-DD HH:mm:ss") carry
// no timezone marker, so browsers would parse them as LOCAL time — force UTC by
// normalizing to ISO and appending 'Z' when no offset is present. The resulting
// Date is then diffed against now / rendered in the viewer's local zone.
const parseServerDate = (v) => {
  if (v == null) return null;
  if (typeof v === 'number') return new Date(v);
  let s = String(v).trim();
  if (!s) return null;
  const hasTz = /[zZ]$/.test(s) || /[+-]\d{2}:?\d{2}$/.test(s);
  s = s.replace(' ', 'T');
  if (!hasTz && s.includes('T')) s += 'Z';
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
};

// Relative "last active" label from a GMT+00 timestamp returned by the API.
const formatLastActive = (v) => {
  const d = parseServerDate(v);
  if (!d) return '';
  const diffMs = Date.now() - d.getTime();
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return 'Active now';
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hour${hr > 1 ? 's' : ''} ago`;
  const day = Math.floor(hr / 24);
  if (day === 1) return 'Yesterday';
  if (day < 7) return `${day} days ago`;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

// Map an API device row → the shape DeviceCard renders. `id` is the session id
// used for revoke; `device_id` is the stable per-browser id (not shown).
const mapDevice = (d) => ({
  id: d.id, // session_id for revoke
  name: d.device_name || 'Unknown device',
  type: (d.device_type || '').toLowerCase(), // WEB/TV/MOBILE → web/tv/mobile
  platform: d.platform || d.app_version || '—',
  location: d.ip_address || '',
  // The current session is live right now (it's the one making this request),
  // so label it "Active now" regardless of the stored last_active timestamp.
  lastActive: d.is_current ? 'Active now' : formatLastActive(d.last_active),
  current: !!d.is_current,
});

const LINK_STEPS = [
  'Open Projector on your TV and choose “Sign in”.',
  'A short login code appears on the screen.',
  'Type that code above and select “Link device”.',
];

const DEVICE_ICON = {
  tv: TvRoundedIcon,
  mobile: SmartphoneRoundedIcon,
  web: ComputerRoundedIcon,
};
const deviceIcon = (type) => DEVICE_ICON[type] || DevicesOtherRoundedIcon;

const DeviceCard = ({ device, onSignOut, busy }) => {
  const Icon = deviceIcon(device.type);
  return (
    <Card sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.25, height: '100%' }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box
          sx={{
            width: 42, height: 42, borderRadius: 2, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'action.hover', color: 'text.secondary',
          }}
        >
          <Icon />
        </Box>
        {device.current ? (
          <Chip size="small" color="primary" label="This device" sx={{ height: 22, fontWeight: 700 }} />
        ) : (
          <Tooltip title="Sign out of this device">
            <span>
              <IconButton size="small" color="error" onClick={() => onSignOut(device)} disabled={busy}>
                {busy ? <CircularProgress size={16} color="inherit" /> : <LogoutRoundedIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Stack>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700 }} noWrap>{device.name}</Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }} noWrap>{device.platform}</Typography>
      </Box>
      <Typography variant="caption" sx={{ color: 'text.secondary', mt: 'auto' }}>
        {[device.location, device.lastActive].filter(Boolean).join('  ·  ')}
      </Typography>
    </Card>
  );
};

export const ManageDeviceV2 = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const autoCode = searchParams.get('code');
  const isAutoMode = Boolean(autoCode);

  const { langText, setAppSnackbar } = useContext(AppContext);
  const manageDeviceText = langText?.manageDevice;

  const [code, setCode] = useState(autoCode || '');
  const [devices, setDevices] = useState(null); // null = loading
  const [signingOutId, setSigningOutId] = useState(null);
  const [revokingOthers, setRevokingOthers] = useState(false);
  const redirectedRef = useRef(false);

  // A TV code in the URL (?code=…) is NOT acted on here any more. It used to
  // link the device on mount, which meant opening the link was enough to
  // attach a TV to the account. Hand off to the consent screen instead, which
  // asks the user to Allow or Decline first.
  useEffect(() => {
    if (!isAutoMode || redirectedRef.current) return;
    redirectedRef.current = true;
    navigate(`${Pathname.tvLoginApprove}?code=${encodeURIComponent(autoCode)}`, { replace: true });
  }, [isAutoMode, autoCode]);

  // Real device list. Token is injected into the body by the Api interceptor.
  const loadDevices = useCallback(async () => {
    try {
      // Bump this session's last_active first so the list reflects it as fresh.
      await sendHeartbeat();
      const res = await dataFetcher(endpoints.listDevices);
      const list = Array.isArray(res?.devices) ? res.devices.map(mapDevice) : [];
      setDevices(list);
    } catch (e) {
      // On failure show the empty state rather than a perpetual spinner.
      setDevices([]);
    }
  }, []);

  useEffect(() => {
    if (isAutoMode) return;
    loadDevices();
  }, [isAutoMode, loadDevices]);

  const codeIsValid = code.trim().length >= 6;

  const { isValidating: linking, mutate: linkDevice } = useSWR([endpoints.registerTv, code], {
    fetcher: (url, inputCode) => dataFetcher(url, { code: inputCode }),
    onSuccess: ({ success, message }) => {
      if (success) {
        setAppSnackbar({ isVisible: true, type: 'success', message: 'Device linked successfully' });
        setCode('');
        // Pull the fresh list so the newly-linked device shows with real data.
        loadDevices();
      } else {
        setAppSnackbar({ isVisible: true, type: 'error', message: message || 'That code didn’t work. Try again.' });
      }
    },
    onError: () => {
      setAppSnackbar({ isVisible: true, type: 'error', message: 'Something went wrong linking the device.' });
    },
  });

  // Revoke a single session. `device.id` is the session_id the API expects.
  const signOutDevice = async (device) => {
    setSigningOutId(device.id);
    try {
      const res = await dataFetcher(endpoints.revokeDevice, { session_id: device.id });
      if (res?.success) {
        setDevices((prev) => (prev || []).filter((d) => d.id !== device.id));
        setAppSnackbar({ isVisible: true, type: 'success', message: res?.message || `Signed out of ${device.name}` });
      } else {
        setAppSnackbar({ isVisible: true, type: 'error', message: res?.message || 'Could not sign out of that device' });
      }
    } catch (e) {
      setAppSnackbar({ isVisible: true, type: 'error', message: 'Could not sign out of that device' });
    } finally {
      setSigningOutId(null);
    }
  };

  // Revoke every session except the current one.
  const signOutOthers = async () => {
    setRevokingOthers(true);
    try {
      const res = await dataFetcher(endpoints.revokeOthersDevice);
      if (res?.success) {
        setAppSnackbar({
          isVisible: true,
          type: 'success',
          message: res?.message || `Signed out of ${res?.revoked ?? 'all other'} device(s)`,
        });
        setDevices((prev) => (prev || []).filter((d) => d.current));
      } else {
        setAppSnackbar({ isVisible: true, type: 'error', message: res?.message || 'Could not sign out other devices' });
      }
    } catch (e) {
      setAppSnackbar({ isVisible: true, type: 'error', message: 'Could not sign out other devices' });
    } finally {
      setRevokingOthers(false);
    }
  };

  const deviceCount = devices?.length || 0;
  const otherCount = (devices || []).filter((d) => !d.current).length;

  // Redirecting to the consent screen — render nothing rather than flashing
  // the device manager for a frame.
  if (isAutoMode) return null;

  return (
    <V2SettingsShell
      title="TV & devices"
      description="Link a TV with a login code and manage the devices signed in to your account."
      // No bottom tab bar on this screen — you get here from Settings and leave
      // through "Back to Settings".
      hideDock
    >
      <Box sx={{
        display: 'grid', gridTemplateColumns: { xs: '1fr', md: '0.85fr 1.7fr' }, gap: 2, alignItems: 'stretch',
      }}
      >
        {/* LEFT: enter login code — full-height card */}
        <Card sx={{ p: { xs: 2.5, md: 3 }, height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center', mb: 0.5 }}>
            <Box sx={{
              width: 40, height: 40, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'primary.main', color: 'primary.contrastText',
            }}
            >
              <AddToQueueRoundedIcon fontSize="small" />
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: 16 }}>Enter login code</Typography>
          </Stack>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2.5 }}>
            On your TV, open Projector and enter the code shown there to link it to your account.
          </Typography>
          <TextField
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\s/g, ''))}
            label="TV code"
            placeholder="e.g. 4X9K2P"
            fullWidth
            inputProps={{ style: { letterSpacing: 3, fontWeight: 700, textTransform: 'uppercase' } }}
            sx={{ mb: 2 }}
          />
          <Button
            variant="contained"
            fullWidth
            size="large"
            disabled={!codeIsValid || linking}
            onClick={() => linkDevice()}
            startIcon={linking ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ borderRadius: 2, fontWeight: 700 }}
          >
            {linking ? (`${manageDeviceText?.addingDevice || 'Linking'}…`) : (manageDeviceText?.addDevice || 'Link device')}
          </Button>

          {/* How-to steps fill the rest of the full-height card. */}
          <Divider sx={{ my: 3 }} />
          <Typography sx={{
            fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: 'text.secondary', mb: 1.5,
          }}
          >
            How to link a TV
          </Typography>
          <Stack spacing={1.75}>
            {LINK_STEPS.map((step, i) => (
              <Stack key={step} direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
                <Box sx={{
                  width: 22, height: 22, flexShrink: 0, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'action.hover', color: 'text.primary', fontSize: 12, fontWeight: 700,
                }}
                >
                  {i + 1}
                </Box>
                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>{step}</Typography>
              </Stack>
            ))}
          </Stack>
        </Card>

        {/* RIGHT: signed-in devices — the accounts/sessions currently linked. */}
        <Box>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline' }}>
              <Typography sx={{ fontWeight: 700, fontSize: 16 }}>Signed-in devices</Typography>
              {devices !== null && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {`${deviceCount} ${deviceCount === 1 ? 'device' : 'devices'}`}
                </Typography>
              )}
            </Stack>
            {otherCount > 0 && (
              <Button
                size="small"
                color="error"
                onClick={signOutOthers}
                disabled={revokingOthers}
                startIcon={revokingOthers ? <CircularProgress size={14} color="inherit" /> : <LogoutRoundedIcon fontSize="small" />}
                sx={{ fontWeight: 700, textTransform: 'none' }}
              >
                Sign out others
              </Button>
            )}
          </Stack>

          {devices === null ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, alignContent: 'start' }}>
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} variant="rounded" height={122} animation="wave" />
              ))}
            </Box>
          ) : deviceCount === 0 ? (
            <Card sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>No devices are signed in right now.</Typography>
            </Card>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, alignContent: 'start' }}>
              {devices.map((d) => (
                <DeviceCard key={d.id} device={d} onSignOut={signOutDevice} busy={signingOutId === d.id} />
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </V2SettingsShell>
  );
};

export default ManageDeviceV2;
